import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './LabellingWizard.module.css';
import type { Algorithm, PlacedLandmark } from '../types';
import type { PluginContext } from '../../types';
import {
  RmaiError,
  findAnnotation,
  registerImage,
  upsertAnnotation,
} from '../rmai';
import { getReaderId, setReaderId } from '../config';
// Auto-detect reuses the dcx-detect client that powers the Chiro
// Auto-detect flow. dcx-detect currently has a real detector for
// tool 300 (Cobb) and a grid-fallback for everything else; the flow
// is identical either way from the reader's perspective — points
// appear pre-placed, reader drags any that are wrong.
import { SUPPORTED_DETECTIONS, autoDetectPoints } from '../../chiroTools/detect';

interface Props {
  algorithm: Algorithm;
  ctx: PluginContext;
  onClose: () => void;
}

interface SaveState {
  state: 'idle' | 'saving' | 'saved' | 'error';
  message?: string;
}

/**
 * Guided-workflow wizard for one (film, algorithm, reader) session.
 *
 * Floating panel + SVG overlay pattern lifted from ChiroWizard. The
 * distinct things this wizard does:
 *   - Landmark TYPES come from the algorithm's `requiredLandmarks` — a
 *     new landmark gets its type from the ordered sequence position.
 *   - Guides the reader by type name ("Click: HARD_PALATE_POSTERIOR").
 *   - Resumes from an existing RMAI annotation for
 *     (imageId, algorithm, reader) if one is present.
 *   - Uploads the DICOM to RMAI (idempotent) on save, then upserts
 *     the annotation.
 */
export default function LabellingWizard({ algorithm, ctx, onClose }: Props) {
  const viewportEl = ctx.getActiveViewportElement();
  const viewport = ctx.getActiveViewport();

  const [points, setPoints] = useState<PlacedLandmark[]>([]);
  const [reader, setReader] = useState<string>(getReaderId());
  const [filmId, setFilmId] = useState<string>('');
  const [measurementValue, setMeasurementValue] = useState<string>('');
  const [imageId, setImageId] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState<boolean>(true);
  const [loadingStep, setLoadingStep] = useState<string>('starting…');
  const [initError, setInitError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<SaveState>({ state: 'idle' });
  const [renderTick, setRenderTick] = useState(0);

  // --- Register the DICOM with RMAI on mount (idempotent — no cache),
  // then check for a prior annotation to resume from. ---
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!viewport) {
        setInitError('No active viewport. Open a study first.');
        setImageLoading(false);
        return;
      }
      try {
        console.log('[Labelling] fetching DICOM bytes from viewport', viewport);
        setLoadingStep('reading imageId from viewport');
        const dicomBytes = await fetchDicomBytes(viewport);
        if (cancelled) return;
        console.log('[Labelling] got DICOM bytes:', dicomBytes.size, 'bytes');
        const totalKB = (dicomBytes.size / 1024).toFixed(0);
        setLoadingStep(`uploading 0 / ${totalKB} KB to RMAI`);
        const registration = await registerImage(dicomBytes, (uploaded, total) => {
          if (cancelled) return;
          const pct = total > 0 ? Math.round((uploaded / total) * 100) : 0;
          setLoadingStep(
            `uploading ${(uploaded / 1024).toFixed(0)} / ${(total / 1024).toFixed(0)} KB (${pct}%) to RMAI`,
          );
        });
        if (cancelled) return;
        console.log('[Labelling] registered image:', registration.imageId);
        setImageId(registration.imageId);
        const sop = registration.image.sopInstanceUid;
        setFilmId(sop.split('.').slice(-2).join('.'));

        setLoadingStep('checking for prior annotation');
        const prior = await findAnnotation(registration.imageId, algorithm.algorithm, reader);
        if (prior && !cancelled) {
          console.log('[Labelling] resuming from prior annotation with', prior.landmarks.length, 'landmarks');
          setPoints(
            prior.landmarks.map((lm) => ({
              landmarkId: lm.landmarkId,
              structure: lm.structure,
              type: lm.type,
              worldX: lm.x,
              worldY: lm.y,
              worldZ: 0,
            })),
          );
          setFilmId(prior.filmId);
          if (prior.measurementValue !== null) {
            setMeasurementValue(String(prior.measurementValue));
          }
        }
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        console.error('[Labelling] init failed:', err);
        setInitError(msg);
      } finally {
        if (!cancelled) setImageLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Wire viewport render events → overlay repaint. ---
  useEffect(() => {
    if (!viewportEl) return;
    const bump = () => setRenderTick((t) => t + 1);
    const events = [
      'CORNERSTONE_IMAGE_RENDERED',
      'CORNERSTONE_CAMERA_MODIFIED',
      'CORNERSTONE_STACK_NEW_IMAGE',
    ];
    for (const e of events) viewportEl.addEventListener(e, bump);
    window.addEventListener('resize', bump);
    return () => {
      for (const e of events) viewportEl.removeEventListener(e, bump);
      window.removeEventListener('resize', bump);
    };
  }, [viewportEl]);

  const clientToWorld = useCallback(
    (clientX: number, clientY: number): { x: number; y: number; z: number } | null => {
      if (!viewport) return null;
      const canvas = viewport.getCanvas?.() as HTMLCanvasElement | undefined;
      if (!canvas) return null;
      const rect = canvas.getBoundingClientRect();
      const world = viewport.canvasToWorld([clientX - rect.left, clientY - rect.top]);
      return { x: world[0], y: world[1], z: world[2] ?? 0 };
    },
    [viewport],
  );

  const worldToScreen = useCallback(
    (p: { worldX: number; worldY: number; worldZ: number }): { x: number; y: number } | null => {
      if (!viewport) return null;
      try {
        const [x, y] = viewport.worldToCanvas([p.worldX, p.worldY, p.worldZ]);
        return { x, y };
      } catch {
        return null;
      }
    },
    // renderTick intentional — re-derives on each camera-modified event.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [viewport, renderTick],
  );

  // --- Drag: pointerdown on an existing point starts a drag; anywhere
  // else places a new one (up to the required-landmark count). ---
  const dragPointIdx = useRef<number | null>(null);

  // Stop Cornerstone's active tool (window/level, zoom, pan) from
  // seeing our clicks and drags. The SVG overlay is portalled INTO
  // the viewport DOM element, so pointer events bubble up to the
  // tool's event handler unless we call stopPropagation. preventDefault
  // suppresses the browser's own drag/gesture defaults during a drag.
  const handleViewportPointerDown = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      e.stopPropagation();
      const hit = (e.target as Element).closest('[data-point-index]');
      if (hit) {
        dragPointIdx.current = Number(hit.getAttribute('data-point-index'));
        try {
          e.currentTarget.setPointerCapture(e.pointerId);
        } catch {
          /* ignore */
        }
        e.preventDefault();
        return;
      }
      if (points.length >= algorithm.requiredLandmarks.length) return;
      const world = clientToWorld(e.clientX, e.clientY);
      if (!world) return;
      const type = algorithm.requiredLandmarks[points.length];
      setPoints((prev) => [
        ...prev,
        {
          landmarkId: type,
          structure: type, // structure defaults to type — see rmai.ts / RMAI's schema
          type,
          worldX: world.x,
          worldY: world.y,
          worldZ: world.z,
        },
      ]);
    },
    [points.length, algorithm.requiredLandmarks, clientToWorld],
  );

  const handleViewportPointerMove = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      const idx = dragPointIdx.current;
      if (idx === null) return;
      e.stopPropagation();
      e.preventDefault();
      const world = clientToWorld(e.clientX, e.clientY);
      if (!world) return;
      setPoints((prev) =>
        prev.map((p, i) => (i === idx ? { ...p, worldX: world.x, worldY: world.y, worldZ: world.z } : p)),
      );
    },
    [clientToWorld],
  );

  const handleViewportPointerUp = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    if (dragPointIdx.current === null) return;
    e.stopPropagation();
    dragPointIdx.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  }, []);

  const undo = () => setPoints((prev) => prev.slice(0, -1));
  const restart = () => setPoints([]);

  const nextType = algorithm.requiredLandmarks[points.length] ?? null;
  const done = points.length >= algorithm.requiredLandmarks.length;

  // --- Auto-detect via dcx-detect ---
  const [detecting, setDetecting] = useState(false);
  const [detectError, setDetectError] = useState<string | null>(null);
  const [detectNote, setDetectNote] = useState<string | null>(null);
  const hasRealDetector = SUPPORTED_DETECTIONS.has(algorithm.toolId);

  const runAutoDetect = useCallback(async () => {
    if (!viewport) return;
    setDetecting(true);
    setDetectError(null);
    setDetectNote(null);
    try {
      const result = await autoDetectPoints(viewport, algorithm.toolId);
      // Map dcx-detect's ordered points onto this algorithm's ordered
      // required-landmark types. If dcx-detect returned more or fewer
      // points than we need, clamp to whichever is shorter — the reader
      // can top up manually by clicking, or Undo excess.
      const n = Math.min(result.points.length, algorithm.requiredLandmarks.length);
      const detected: PlacedLandmark[] = [];
      for (let i = 0; i < n; i++) {
        const type = algorithm.requiredLandmarks[i];
        detected.push({
          landmarkId: type,
          structure: type,
          type,
          worldX: result.points[i].worldX,
          worldY: result.points[i].worldY,
          worldZ: result.points[i].worldZ,
        });
      }
      setPoints(detected);
      if (result.fellBack || !hasRealDetector) {
        setDetectNote(
          'No trained AI detector for this algorithm yet — placed generic starter points. Drag each onto its correct anatomy.',
        );
      } else {
        setDetectNote(
          `Detected via ${result.method}. Drag any point to refine before saving.`,
        );
      }
    } catch (err) {
      setDetectError(err instanceof Error ? err.message : String(err));
    } finally {
      setDetecting(false);
    }
  }, [viewport, algorithm.toolId, algorithm.requiredLandmarks, hasRealDetector]);

  // --- Save ---
  const save = useCallback(async () => {
    if (!imageId) {
      setSaveState({ state: 'error', message: 'No image registered yet.' });
      return;
    }
    if (!filmId.trim()) {
      setSaveState({ state: 'error', message: 'Film ID is required.' });
      return;
    }
    if (!reader.trim()) {
      setSaveState({ state: 'error', message: 'Reader ID is required.' });
      return;
    }
    setReaderId(reader);
    setSaveState({ state: 'saving' });
    try {
      const parsedValue = measurementValue.trim() ? Number(measurementValue) : null;
      if (parsedValue !== null && Number.isNaN(parsedValue)) {
        setSaveState({
          state: 'error',
          message: `Measurement value must be a number (${algorithm.unit}) or blank.`,
        });
        return;
      }
      await upsertAnnotation({
        imageId,
        filmId: filmId.trim(),
        algorithm: algorithm.algorithm,
        reader: reader.trim(),
        landmarks: points.map((p) => ({
          landmarkId: p.landmarkId,
          structure: p.structure,
          type: p.type,
          x: p.worldX,
          y: p.worldY,
        })),
        measurementValue: parsedValue,
      });
      setSaveState({ state: 'saved', message: `Saved ${points.length} landmarks.` });
    } catch (err) {
      const msg =
        err instanceof RmaiError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err);
      setSaveState({ state: 'error', message: msg });
    }
  }, [imageId, filmId, reader, measurementValue, algorithm, points]);

  const overlaySize = useMemo(() => {
    if (!viewport) return { w: 0, h: 0 };
    const canvas = viewport.getCanvas?.() as HTMLCanvasElement | undefined;
    if (!canvas) return { w: 0, h: 0 };
    const rect = canvas.getBoundingClientRect();
    return { w: rect.width, h: rect.height };
    // Include renderTick so resize / camera events resize the overlay too.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewport, renderTick]);

  return (
    <>
      <div className={styles.panel}>
        <div className={styles.header}>
          <h2 className={styles.title}>
            {algorithm.name}
            <span className={styles.stepInline}>
              {` — ${Math.min(points.length + 1, algorithm.requiredLandmarks.length)}/${algorithm.requiredLandmarks.length}`}
            </span>
          </h2>
          <div className={styles.headerActions}>
            <button
              className={styles.autoDetectBtn}
              onClick={runAutoDetect}
              disabled={detecting || imageLoading || !viewport || !imageId}
              title={
                hasRealDetector
                  ? 'Run AI detection on this image'
                  : 'No trained AI detector for this algorithm yet — will place generic starter points to drag'
              }
            >
              {detecting ? 'Detecting…' : 'Auto-detect'}
            </button>
            <button className={styles.close} onClick={onClose} title="Close">
              ×
            </button>
          </div>
        </div>

        {imageLoading && <div className={styles.info}>Registering image with RMAI: {loadingStep}…</div>}
        {initError && (
          <div className={styles.error}>Init failed: {initError}</div>
        )}
        {detectError && (
          <div className={styles.error}>
            Auto-detect failed: {detectError}. You can still click points manually.
          </div>
        )}
        {detectNote && !detectError && (
          <div className={styles.info}>{detectNote}</div>
        )}

        <div className={styles.fieldRow}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Reader</span>
            <input
              className={styles.fieldInput}
              type="text"
              value={reader}
              onChange={(e) => setReader(e.target.value)}
              placeholder="reader-a"
            />
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Film ID</span>
            <input
              className={styles.fieldInput}
              type="text"
              value={filmId}
              onChange={(e) => setFilmId(e.target.value)}
              placeholder="CS2"
            />
          </label>
        </div>

        {!done && nextType && (
          <div className={styles.prompt}>
            Click: <code>{nextType}</code>
          </div>
        )}
        {done && (
          <div className={styles.done}>All landmarks placed. Drag any point to adjust.</div>
        )}

        <div className={styles.fieldRow}>
          <label className={styles.field} style={{ flex: 1 }}>
            <span className={styles.fieldLabel}>
              Measurement value ({algorithm.unit.toLowerCase()})
            </span>
            <input
              className={styles.fieldInput}
              type="text"
              inputMode="decimal"
              value={measurementValue}
              onChange={(e) => setMeasurementValue(e.target.value)}
              placeholder="e.g. 2.1 — leave blank to skip"
            />
          </label>
        </div>

        {saveState.state === 'error' && (
          <div className={styles.error}>{saveState.message}</div>
        )}
        {saveState.state === 'saved' && (
          <div className={styles.success}>{saveState.message ?? 'Saved.'}</div>
        )}

        <div className={styles.footer}>
          <button className={styles.btn} onClick={undo} disabled={points.length === 0}>
            Undo
          </button>
          <button className={styles.btn} onClick={restart} disabled={points.length === 0}>
            Clear
          </button>
          <button
            className={`${styles.btn} ${styles.primary}`}
            onClick={save}
            disabled={saveState.state === 'saving' || !imageId}
          >
            {saveState.state === 'saving' ? 'Saving…' : 'Save'}
          </button>
          <button className={styles.btn} onClick={onClose}>
            Close
          </button>
        </div>
      </div>

      {viewportEl &&
        viewport &&
        createPortal(
          <svg
            className={styles.viewportOverlay}
            width={overlaySize.w}
            height={overlaySize.h}
            viewBox={`0 0 ${overlaySize.w} ${overlaySize.h}`}
            onPointerDown={handleViewportPointerDown}
            onPointerMove={handleViewportPointerMove}
            onPointerUp={handleViewportPointerUp}
            onPointerCancel={handleViewportPointerUp}
          >
            {points.map((p, i) => {
              const s = worldToScreen(p);
              if (!s) return null;
              return (
                <g key={`p${i}`} data-point-index={i} style={{ cursor: 'grab' }}>
                  <circle cx={s.x} cy={s.y} r={14} fill="transparent" stroke="none" />
                  <circle
                    cx={s.x}
                    cy={s.y}
                    r={5}
                    fill="#4ade80"
                    stroke="#000"
                    strokeWidth={1}
                    style={{ pointerEvents: 'none' }}
                  />
                  <text
                    x={s.x + 8}
                    y={s.y + 4}
                    fill="#4ade80"
                    fontSize={11}
                    fontWeight={700}
                    style={{
                      pointerEvents: 'none',
                      paintOrder: 'stroke',
                      stroke: '#000',
                      strokeWidth: 3,
                    }}
                  >
                    {`${i + 1} · ${p.type}`}
                  </text>
                </g>
              );
            })}
          </svg>,
          viewportEl,
        )}
    </>
  );
}

/**
 * Pull the raw DICOM bytes for the currently-displayed image out of the
 * viewport. We deliberately re-fetch rather than reaching into
 * Cornerstone's internal cache — the cache holds a decoded pixel array,
 * not the original DICOM stream RMAI needs.
 *
 * The imageId format depends on the loader:
 *   - `wadouri:https://.../image.dcm` — EDGEPACS (this repo's default)
 *   - `wadors:https://.../frames/1`   — DICOMweb (multipart/related)
 *
 * v1 handles wadouri directly. wadors is a multipart response and needs
 * unwrapping — not implemented yet; if you're on a DICOMweb backend the
 * wizard will surface the error and the user can fall back to the CLI
 * (scripts/label.py) meanwhile.
 */
async function fetchDicomBytes(viewport: any): Promise<Blob> {
  // Try every Cornerstone3D v5 accessor we know of. Different viewport
  // types (StackViewport, VolumeViewport, etc.) expose the imageId in
  // subtly different places. We log which one succeeded so a mystery
  // hang shows up as "hmm none of these returned anything" in the console.
  let imageId: string | undefined;
  const candidates: Array<() => string | undefined> = [
    () => viewport.getCurrentImageId?.(),
    () => viewport.getImageIds?.()?.[viewport.getCurrentImageIdIndex?.() ?? 0],
    () => viewport.imageIds?.[viewport.getCurrentImageIdIndex?.() ?? 0],
    () => viewport.currentImageId,
    () => viewport.getReferenceImageId?.(),
  ];
  for (const [i, get] of candidates.entries()) {
    try {
      const v = get();
      if (typeof v === 'string' && v.length > 0) {
        imageId = v;
        console.log(`[Labelling] resolved imageId via candidate ${i}:`, imageId.slice(0, 80));
        break;
      }
    } catch {
      /* try next */
    }
  }
  if (!imageId) {
    console.error('[Labelling] viewport had no imageId. viewport keys:', Object.keys(viewport));
    throw new Error(
      "viewport has no current image (couldn't find imageId via any known Cornerstone3D accessor). " +
        'Check the browser console for the viewport keys dump.',
    );
  }

  console.log('[Labelling] fetching DICOM from URL scheme:', imageId.slice(0, 12));

  if (imageId.startsWith('wadouri:')) {
    const url = imageId.slice('wadouri:'.length);
    // Race with a 15-second timeout so a hung fetch doesn't leave the
    // wizard stuck at "Registering image with RMAI…" forever.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url, { signal: controller.signal });
      clearTimeout(timer);
      if (!response.ok) throw new Error(`fetch DICOM failed (HTTP ${response.status})`);
      return await response.blob();
    } catch (err) {
      clearTimeout(timer);
      if (err instanceof Error && err.name === 'AbortError') {
        throw new Error(
          'timed out fetching DICOM from EDGEPACS (15s). The DICOM URL from Cornerstone did not respond.',
        );
      }
      throw err;
    }
  }

  if (imageId.startsWith('wadors:')) {
    throw new Error(
      'DICOMweb (wadors) image bytes fetch is not implemented yet in the labelling plugin. ' +
        'Use scripts/label.py + POST /images from the CLI until this lands.',
    );
  }

  throw new Error(`unsupported imageId scheme: ${imageId.slice(0, 40)}`);
}
